import React, { useState, useEffect } from 'react';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import toast from 'react-hot-toast';
import {
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

import { API_BASE } from '../lib/config';
// API_BASE は既に "/api" を含むため、二重付与を避ける
const API = API_BASE;

const CardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const CardRegistrationForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [fakeMode, setFakeMode] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    // ローカルの不具合よけ: 事業者でログインしているかチェック
    
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed?.user_type !== 'employer') {
          toast.error('事業者アカウントでログインしてください（カード登録は事業者のみ可能）');
          return;
        }
      }
    } catch {}

    // Stripeキー未設定だが、バックエンドがフェイクモードなら継続可

    // トークン取得
    let token = null;
    try { token = localStorage.getItem('token') || localStorage.getItem('admin_token'); } catch {}
    if (!token) {
      toast.error('ログインが必要です。事業者としてログインしてください。');
      return;
    }

    setLoading(true);

    try {
      // Setup Intentを作成
      const setupResponse = await fetch(`${API}/employers/stripe/setup-intent`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({})
      });

      if (!setupResponse.ok) {
        let detail = 'Failed to create setup intent';
        try { const j = await setupResponse.json(); detail = j?.detail || detail; } catch {}
        throw new Error(detail);
      }

      const { client_secret, setup_intent_id } = await setupResponse.json();

      // バックエンドがStripe無効時はフェイクIDを返す: seti_fake_*/secret_seti_fake_*
      const isFake = (setup_intent_id && /^seti_fake_/i.test(setup_intent_id)) || (client_secret && /^secret_seti_fake_/i.test(client_secret));
      setFakeMode(!!isFake);

      if (isFake) {
        // フェイクモード: クライアントでのStripe確認は不要。直接サーバに確定通知
        const confirmResponse = await fetch(`${API}/employers/stripe/confirm-setup`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ setup_intent_id })
        });
        if (confirmResponse.ok) {
          toast.success('クレジットカードが登録されました（ローカルモード）');
          onSuccess();
          return;
        }
        let detail = 'Failed to confirm setup';
        try { const j = await confirmResponse.json(); detail = j?.detail || detail; } catch {}
        toast.error(detail || 'クレジットカードの登録に失敗しました');
        return;
      }

      // ここから実Stripe
      if (!stripe) {
        toast.error('Stripeの初期化に失敗しました');
        return;
      }
      const { error } = await stripe.confirmCardSetup(
        client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: { name: 'Business User' },
          }
        }
      );

      if (error) {
        console.error('Card setup error:', error);
        const msg = (error?.code === 'expired_card' || /expired/i.test(error?.message || ''))
          ? 'このクレジットカードの有効期限が切れています。別のカードをご利用ください。'
          : (error?.message || 'クレジットカードの登録に失敗しました');
        toast.error(msg);
        return;
      }

      // 成功時：バックエンドに登録完了を通知
      const confirmResponse = await fetch(`${API}/employers/stripe/confirm-setup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ setup_intent_id })
      });

      if (confirmResponse.ok) {
        toast.success('クレジットカードが登録されました');
        onSuccess();
      } else {
        let detail = 'Failed to confirm setup';
        try { const j = await confirmResponse.json(); detail = j?.detail || detail; } catch {}
        if (/expired/i.test(detail)) {
          toast.error('このクレジットカードの有効期限が切れています。別のカードをご利用ください。');
        } else {
          toast.error(detail || 'クレジットカードの登録に失敗しました');
        }
        return;
      }
    } catch (error) {
      console.error('Registration error:', error);
      const msg = /expired/i.test(error?.message || '')
        ? 'このクレジットカードの有効期限が切れています。別のカードをご利用ください。'
        : (error?.message || 'クレジットカードの登録に失敗しました');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <h2 className="text-xl font-semibold mb-4">クレジットカード登録</h2>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              カード情報
            </label>
            <div className="border border-gray-300 rounded-lg p-3">
              <CardElement options={CardElementOptions} />
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex">
              <CreditCardIcon className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-semibold mb-1">Stripe セキュリティについて</p>
                <ul className="text-xs space-y-1">
                  <li> カード情報はStripeにより暗号化されて保存されます</li>
                  <li> 当社サーバーにはカード情報は保存されません</li>
                  <li> PCI DSS準拠の最高レベルのセキュリティを使用</li>
                  <li> 支払い時にのみカードが使用されます</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              disabled={!stripe || loading}
              className="flex-1 bg-blue-500 text-white py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? '登録中...' : '登録'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-500 text-white py-2 rounded-lg font-medium hover:bg-gray-600 transition-colors"
            >
              キャンセル
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const CreditCardRegistration = ({ onCardRegistered, initialOpen = false }) => {
  const [cards, setCards] = useState([]);
  const [paymentReady, setPaymentReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [stripePromise, setStripePromise] = useState(null);
  const parentStripe = useStripe();

  useEffect(() => {
    fetchCards();
    checkPaymentReady();
    if (initialOpen) {
      // 初回表示時に自動でモーダルを開く
      setShowRegistrationForm(true);
    }
    // Stripeキーが未設定ならバックエンドから取得して初期化
    (async () => {
      try {
        if (!stripePromise) {
          const res = await fetch(`${API}/stripe/publishable-key?t=${Date.now()}`);
          if (res.ok) {
            const { publishable_key } = await res.json();
            if (publishable_key) setStripePromise(loadStripe(publishable_key));
          }
        }
      } catch (e) {}
    })();
  }, []);

  const fetchCards = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const response = await fetch(`${API}/employers/stripe/cards`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const cardsData = await response.json();
        setCards(cardsData);
      }
    } catch (error) {
      console.error('Error fetching cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentReady = async () => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const response = await fetch(`${API}/employers/stripe/payment-ready`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentReady(data.payment_ready);
      }
    } catch (error) {
      console.error('Error checking payment ready:', error);
    }
  };

  const handleDeleteCard = async (cardId) => {
    if (!window.confirm('このクレジットカードを削除しますか？')) {
      return;
    }

    try {
      const token = localStorage.getItem('token') || localStorage.getItem('admin_token');
      const response = await fetch(`${API}/employers/stripe/cards/${cardId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('クレジットカードを削除しました');
        fetchCards();
        checkPaymentReady();
        if (onCardRegistered) onCardRegistered();
      } else {
        toast.error('削除に失敗しました');
      }
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error('削除に失敗しました');
    }
  };

  const handleRegistrationSuccess = () => {
    setShowRegistrationForm(false);
    fetchCards();
    checkPaymentReady();
    if (onCardRegistered) onCardRegistered();
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
        <div className="h-10 bg-gray-200 rounded"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">決済方法</h3>
        <button
          onClick={() => setShowRegistrationForm(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          カードを追加
        </button>
      </div>

      {/* 支払い準備状況 */}
      <div className={`mb-4 p-4 rounded-lg border ${
        paymentReady 
          ? 'bg-green-50 border-green-200' 
          : 'bg-yellow-50 border-yellow-200'
      }`}>
        <div className="flex items-center">
          {paymentReady ? (
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
          ) : (
            <XCircleIcon className="h-5 w-5 text-yellow-600 mr-2" />
          )}
          <span className={`font-medium ${
            paymentReady ? 'text-green-800' : 'text-yellow-800'
          }`}>
            {paymentReady ? 'ワンクリック決済準備完了' : 'クレジットカード登録が必要です'}
          </span>
        </div>
        <p className={`text-sm mt-1 ${
          paymentReady ? 'text-green-700' : 'text-yellow-700'
        }`}>
          {paymentReady 
            ? '登録されたクレジットカードで即座に求職者への支払いが可能です' 
            : '求職者に支払いを行うには、まずクレジットカードの登録が必要です'
          }
        </p>
      </div>

      {/* 登録済みカード一覧 */}
      {cards.length > 0 ? (
        <div className="space-y-3">
          {cards.map((card) => (
            <div key={card.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center">
                  <CreditCardIcon className="h-6 w-6 text-gray-400 mr-3" />
                  <div>
                    <div className="font-medium text-gray-900">
                      {card.card_brand.toUpperCase()}  {card.card_last4}
                    </div>
                    <div className="text-sm text-gray-500">
                      有効期限: {card.card_exp_month.toString().padStart(2, '0')}/{card.card_exp_year}
                    </div>
                    {/* 期限切れ警告 */}
                    {(() => {
                      try {
                        const now = new Date();
                        const y = parseInt(card.card_exp_year, 10);
                        const m = parseInt(card.card_exp_month, 10);
                        const expired = (y < now.getFullYear()) || (y === now.getFullYear() && m < (now.getMonth()+1));
                        return expired ? (
                          <div className="text-xs text-red-600 mt-1">このカードは有効期限切れです。別のカードを登録してください。</div>
                        ) : null;
                      } catch { return null; }
                    })()}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteCard(card.id)}
                  className="text-red-600 hover:text-red-800"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <CreditCardIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">クレジットカードが登録されていません</p>
          <button
            onClick={() => setShowRegistrationForm(true)}
            className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors"
          >
            最初のカードを登録
          </button>
        </div>
      )}

      {/* カード登録フォーム */}
      {showRegistrationForm && (
        parentStripe ? (
          <CardRegistrationForm
            onSuccess={handleRegistrationSuccess}
            onCancel={() => setShowRegistrationForm(false)}
          />
        ) : stripePromise ? (
          <Elements stripe={stripePromise}>
            <CardRegistrationForm
              onSuccess={handleRegistrationSuccess}
              onCancel={() => setShowRegistrationForm(false)}
            />
          </Elements>
        ) : (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
            <p className="text-sm text-yellow-700">Stripeの公開鍵が未設定のため、カード入力フィールドを表示できません。ページを再読み込みしてください。</p>
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowRegistrationForm(false)}
                className="bg-gray-500 text-white px-4 py-2 rounded"
              >閉じる</button>
            </div>
          </div>
        )
      )}
    </div>
  );
};

export default CreditCardRegistration;